import { format } from 'date-fns';
import axios from 'helpers/axios';
import { truncate } from 'helpers/parser';
import { useCancelToken, useMutableRef, useOpenState, useToasts } from 'hooks';
import React from 'react';
import { Card, CardGrid, Dot, EmailModal, IconButton } from 'ui/components/shared';
import { getNumberOfPages, recalculatePage } from 'ui/components/shared/DataTable/helpers';
import { ConfirmationModal, Container, Label, Paragraph } from 'ui/components/styled';


export const EmailsList = React.forwardRef(({ job, containerRef }, ref) => { // eslint-disable-line
  const [data, setData] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [total, setTotal] = React.useState(0);
  const [perPage] = React.useState(10);
  const [search, setSearch] = React.useState('');
  const [modal, openModal, closeModal] = useOpenState();
  const { errorToast, removeAllToasts } = useToasts();
  const [currentPage, setCurrentPage] = useMutableRef(1);
  const { newCancelToken, cancelPreviousRequest, isCancel } = useCancelToken();

  const fetchEmails = async (page) => {
    setLoading(true);

    try {
      const top = page * perPage;
      const skip = 0;
      const response = await axios.get(
        `/jobs/${job?.id}/emails/?$top=${top}&$skip=${skip}&$search=${search}&$orderby=date&$order=desc`
      );

      if (response.status === 200) {
        setData(response.data.value);
        setTotal(response.data.count);
      }
    } catch (error) {
      errorToast({ error, default: 'Error: Fetch Emails.' });
    }

    setLoading(false);
  };

  const handleFilter = async (value) => {
    setLoading(true);
    setData([]);
    setCurrentPage(1);
    setSearch(value);

    cancelPreviousRequest();

    try {
      const skip = 0;
      const response = await axios.get(
        `/jobs/${job?.id}/emails/?$top=${perPage}&$skip=${skip}&$search=${value}&$orderby=date&$order=desc`,
        { cancelToken: newCancelToken() }
      );

      if (response.status === 200) {
        setData(response.data.value);
        setTotal(response.data.count);
      }
    } catch (error) {
      if (isCancel(error)) return;
      errorToast({ error, default: 'Error: Filter Emails.' });
    }

    setLoading(false);
  };

  const unlinkEmail = async (email) => {
    try {
      const response = await axios.post(`/emails/${email.id}/unlink/${job?.id}`);

      if (response.status === 200) {
        removeAllToasts();
        refresh(total - 1);
      }
    } catch (error) {
      errorToast({ error, default: 'Error: Unlink Email.' });
    }

    closeModal();
  };

  const refresh = (newCount) => {
    if (newCount > 0) {
      const updatedPage = getNumberOfPages(newCount, perPage);
      const recalculatedPage = recalculatePage(currentPage.current, updatedPage);
      if (recalculatedPage !== currentPage.current) {
        setCurrentPage(recalculatedPage);
      }
    }

    fetchData(currentPage.current);
  };

  const handleLoadMore = () => {
    fetchData(currentPage.current + 1);
  };

  const fetchData = (page = 1) => {
    setCurrentPage(page);
    if (page === 1) {
      setData([]);
    }
    fetchEmails(page);
  };

  React.useImperativeHandle(ref, () => ({
    refresh,
  }));

  React.useEffect(() => {
    fetchData();
  }, []);

  const renderCard = React.useCallback(
    (email) => (
      <Card height="200px" cursor="pointer" onClick={() => openModal('view-email', { email })}>
        <Container flexDirection="row" gap="15px" px="m" pl="s" py="m">
          <Container width="auto" gap="15px" pt="3px">
            <Dot status={email.status} title={email.status} size={14} cursor="initial" />
            <IconButton
              icon="unlink-2"
              variant="transparent"
              size="16px"
              onClick={(event) => {
                event.stopPropagation();
                openModal('confirm-unlink-email', { email });
              }}
            />
          </Container>
          <Container>
            <Container flexDirection="row" alignItems="center" height="auto" mb="xs">
              <Label mr="xxs">From:</Label>
              <Paragraph>{email.from}</Paragraph>
            </Container>
            <Container flexDirection="row" alignItems="center" height="auto" mb="xs">
              <Label mr="xxs">Subject:</Label>
              <Paragraph>{email.subject}</Paragraph>
            </Container>
            <Container flexDirection="row" alignItems="center" height="auto" mb="s">
              <Label mr="xxs">Received:</Label>
              <Paragraph>{format(new Date(email.date), 'MMMM do yyyy, h:mm:ss aaa')}</Paragraph>
            </Container>
            <Paragraph lineHeight="24px" lineClamp={3}>
              {truncate(email.bodyAsText || email.body)}
            </Paragraph>
          </Container>
        </Container>
      </Card>
    ),
    []
  );

  return (
    <>
      <CardGrid
        data={data}
        total={total}
        loading={loading}
        headerTitle={`Recent Emails (${total})`}
        headerTitleProps={{ variant: 'darkGrey', fontSize: '20px', fontWeight: 'bold', mr: 'm' }}
        headerDisposition="actions-first"
        headerProps={{
          justifyContent: 'initial',
          countProps: { pull: 'right' },
        }}
        cardRenderer={renderCard}
        filterPlaceholder="Search"
        filterWidth="30%"
        filterValue={search}
        onFilter={handleFilter}
        onLoadMore={handleLoadMore}
        currentPage={currentPage.current}
        containerRef={containerRef}
        overflow="initial"
        showCount
      />
      <EmailModal
        show={modal.isOpen('view-email')}
        onClose={closeModal}
        email={modal.params?.email}
      />
      <ConfirmationModal
        show={modal.isOpen('confirm-unlink-email')}
        onClose={closeModal}
        onConfirm={() => unlinkEmail(modal.params?.email)}
      >
        Are you sure you want to unlink this email ?
      </ConfirmationModal>
    </>
  );
});
